import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';

import { useUpdateUserMutation } from 'api/main';
import { useCheckVerificationCodeMutation, useSendInvestorProfileMutation } from 'api/tunnelSteps';
import { tunnelSelectors, userSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import {
    initCompanyContactInfoPayload,
    initContactInfoPM,
    initContactInfoPP,
} from 'schemas/tunnel';
import { activities } from 'utils/formData';
import { investorErrors, RoleNames } from 'utils/constants';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import ContactInfoFormPP from './ContactInfoFormPP';
import ContactInfoFormPM from './ContactInfoFormPM';
import {
    useHandleGeneralInfoChange,
    useHandleFileUpload,
    useTunnelChangeHandlers,
} from 'utils/hooks';

const ContactInfoPage = ({
    isResubmit,
    hasStatusOk,
    isStepVerified,
    isPhoneVerified,
    handleNextStep,
    isOnClientSpace,
    isOnAdminSpace,
    specificSubscription,
    specificUser,
    refetch,
}) => {
    const mounted = useRef(false);
    const dispatch = useDispatch();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const [updateUser, { isSuccess, isLoading: updateLoader, error, isError, reset }] =
        useUpdateUserMutation();
    const [
        sendInvestorProfile,
        {
            isSuccess: investorProfileSuccess,
            isLoading: submitLoader,
            error: investorProfileError,
            reset: resetInvestorProfile,
        },
    ] = useSendInvestorProfileMutation();
    const [, { isLoading: codeLoader }] = useCheckVerificationCodeMutation({
        fixedCacheKey: 'code-check',
    });

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelErrors = useSelector(tunnelSelectors.getTunnelErrors);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const subscription = useMemo(
        () =>
            isOnAdminSpace
                ? specificSubscription
                : isOnClientSpace
                ? currentSubscription
                : tunnelSubscription,
        [
            tunnelSubscription,
            currentSubscription,
            isOnClientSpace,
            specificSubscription,
            isOnAdminSpace,
        ]
    );

    const defaultForm = useMemo(() => {
        if (subscription?.role?.name === RoleNames.PP_PROFESSIONNEL) {
            return initContactInfoPP(
                isOnAdminSpace ? specificUser : isResubmit ? internalUser : undefined,
                subscription
            );
        }
        return initContactInfoPM(
            isOnAdminSpace ? specificUser : isResubmit ? internalUser : undefined
        );
    }, [internalUser, isResubmit, subscription, isOnAdminSpace, specificUser]);

    const [generalInfo, setGeneralInfo, handleGeneralInfoChange] = useHandleGeneralInfoChange(
        defaultForm.generalInfo
    );
    const [address, setAddress] = useState(defaultForm.address);
    const [hasDifferentFiscalAddress, setHasDifferentFiscalAddress] = useState(
        defaultForm.hasDifferentFiscalAddress
    );
    const [fiscalAddress, setFiscalAddress] = useState(defaultForm.fiscalAddress);
    const [situation, setSituation] = useState(defaultForm.situation);
    const [fiscalSituation, setFiscalSituation] = useState(defaultForm.fiscalSituation);
    const [fiscalObligations, setFiscalObligations] = useState(defaultForm.fiscalObligations);
    const [politicallyExposed, setPoliticallyExposed] = useState(defaultForm.politicallyExposed);
    const [conditions, setConditions] = useState(defaultForm.conditions);

    const defaultCorporateInfo = useMemo(
        () =>
            initCompanyContactInfoPayload(
                specificUser || internalUser,
                isResubmit ? subscription : undefined
            ),
        [isResubmit, subscription, internalUser, specificUser]
    );
    const [corporateInfo, setCorporateInfo] = useState(defaultCorporateInfo);
    const [fileSizeError, fileTypeError, handleFileUpload] = useHandleFileUpload(setCorporateInfo);

    useEffect(() => {
        if (!mounted.current) return;
        setGeneralInfo(defaultForm.generalInfo);
        setAddress(defaultForm.address);
        setHasDifferentFiscalAddress(defaultForm.hasDifferentFiscalAddress);
        setFiscalAddress(defaultForm.fiscalAddress);
        setSituation(defaultForm.situation);
        setFiscalSituation(defaultForm.fiscalSituation);
        setFiscalObligations(defaultForm.fiscalObligations);
        setPoliticallyExposed(defaultForm.politicallyExposed);
        setConditions(defaultForm.conditions);
        setCorporateInfo(defaultCorporateInfo);
    }, [defaultForm, defaultCorporateInfo, setGeneralInfo]);

    const [errors, setErrors] = useState({
        birthDate: '',
        birthPlace: '',
        department: '',

        phoneNumber: '',
        address: '',
        zipCode: '',
        city: '',

        noOfPersons: '',
        profession: '',

        isUSPerson: '',

        tin: '',
    });

    const defaultSchema = useMemo(
        () =>
            subscription?.indirectUntransferred
                ? {
                      phoneNumber: Joi.string().required().invalid('+').messages({
                          'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
                          'any.invalid': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
                      }),
                  }
                : {
                      birthDate: Joi.string().required().messages({
                          'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_DATE_DE_NAISSANCE,
                      }),
                      birthPlace: Joi.string().required().messages({
                          'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_VILLE_DE_NAISSANCE,
                      }),
                      department: Joi.string().required().messages({
                          'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_DEPARTEMENT_DE_NAISSANCE,
                      }),
                      phoneNumber: Joi.string().required().invalid('+').messages({
                          'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
                          'any.invalid': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
                      }),
                      address: {
                          address: Joi.string()
                              .required()
                              .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS }),
                          city: Joi.string()
                              .required()
                              .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE }),
                          zipCode: Joi.string()
                              .required()
                              .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL }),
                      },
                      profession: Joi.string()
                          .required()
                          .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PROFESSION }),
                  },
        [subscription]
    );

    const validateDefaultSchema = useCallback(() => {
        let { valid, newErrors } = validateSchema(defaultSchema, {
            ...generalInfo,
            address,
            hasDifferentFiscalAddress,
            fiscalAddress,
            ...situation,
            ...fiscalSituation,
            fiscalObligations,
            ...politicallyExposed,
            ...conditions,
        });
        setErrors(newErrors);

        if (
            (!generalInfo.dateOfBirth || !generalInfo.dateOfBirth.isValid()) &&
            !subscription?.indirectUntransferred
        ) {
            valid = false;
            setErrors(prevState => ({ ...prevState, birthDate: texts.FORM_HAS_ERRORS }));
        }
        return valid;
    }, [
        defaultSchema,
        generalInfo,
        address,
        hasDifferentFiscalAddress,
        fiscalAddress,
        situation,
        fiscalSituation,
        fiscalObligations,
        politicallyExposed,
        conditions,
        subscription,
    ]);

    const areDefaultFieldsCompleted =
        generalInfo.birthDate &&
        generalInfo.birthPlace &&
        generalInfo.department &&
        generalInfo.nativeCountry &&
        generalInfo.nationality &&
        generalInfo.phoneNumber &&
        generalInfo.phoneNumber !== '+' &&
        address.address &&
        address.city &&
        address.zipCode &&
        address.country &&
        situation.profession &&
        fiscalSituation.taxResident &&
        fiscalSituation.isUSPerson;

    const handleAdditionalErrors = additionalErrors => {
        setErrors(prevState => ({ ...prevState, ...additionalErrors }));
    };

    const handleAddressChange = useCallback(changedAddress => {
        setAddress(prevValue => ({ ...prevValue, ...changedAddress }));
    }, []);

    const toggleHasDifferentFiscalAddress = useCallback(() => {
        setHasDifferentFiscalAddress(prevValue => !prevValue);
        setFiscalAddress(prevValue => ({
            ...prevValue,
            address: '',
            city: '',
            zipCode: '',
            country: 'FR',
            formattedAddress: null,
        }));
    }, []);

    const handleFiscalAddressChange = useCallback(changedAddress => {
        setFiscalAddress(prevValue => ({ ...prevValue, ...changedAddress }));
    }, []);

    const handleSituationChange = useCallback((name, value) => {
        setSituation(prevValue => ({ ...prevValue, [name]: value }));
    }, []);

    const handleFiscalSituationChange = useCallback((name, value) => {
        setFiscalSituation(prevValue => {
            if (name === 'wasUSFiscalResident') {
                return { ...prevValue, [name]: value, tin: '' };
            }
            return { ...prevValue, [name]: value };
        });
        if (name === 'fiscalObligationOtherCountry') {
            setFiscalObligations([{ country: '', nif: '', nifReason: '' }]);
        }
    }, []);

    const handleAddFiscalObligation = useCallback(() => {
        setFiscalObligations(prevValue => [...prevValue, { country: '', nif: '', nifReason: '' }]);
    }, []);

    const handleRemoveFiscalObligation = useCallback((e, index) => {
        setFiscalObligations(prevValue => {
            if (prevValue.length === 1) {
                return [{ country: '', nif: '', nifReason: '' }];
            }
            return [...prevValue.slice(0, index), ...prevValue.slice(index + 1)];
        });
    }, []);

    const handleFiscalObligationChange = useCallback((e, index) => {
        const name = e.target.name;
        const value = name === 'nif' ? e.target.value.replace(/\s/gi, '') : e.target.value;
        setFiscalObligations(prevValue => [
            ...prevValue.slice(0, index),
            {
                ...prevValue[index],
                [name]: value,
            },
            ...prevValue.slice(index + 1),
        ]);
    }, []);

    const handleChangePoliticallyExposedReason = useCallback((e, index) => {
        const { checked } = e.target;
        setPoliticallyExposed(prevValue => ({
            ...prevValue,
            politicallyExposedReason: { ...prevValue.politicallyExposedReason, [index]: checked },
        }));
    }, []);

    const handleChangePoliticallyExposedPerson = useCallback(e => {
        const { name, value } = e.target;
        setPoliticallyExposed(prevValue => ({
            ...prevValue,
            [name]: value,
            politicallyExposedReason: { 1: false, 2: false, 3: false },
        }));
    }, []);

    const handleConditionsChange = useCallback(e => {
        const { name, checked } = e.target;
        setConditions(prevValue => ({ ...prevValue, [name]: checked }));
    }, []);

    const handleCorporateInfoChange = useCallback(
        (name, value) => {
            setCorporateInfo(prevValue => ({
                ...prevValue,
                [name]: value,
            }));

            if (name === 'activity' && value !== activities.other) {
                setCorporateInfo(prevValue => ({ ...prevValue, otherActivity: '' }));
            }

            if (name === 'signatoryOnKbis') {
                // reset the role, name and document of the representative when changing this option
                setCorporateInfo(oldValue => ({
                    ...oldValue,
                    representativeRole: '',
                    representativeName: '',
                    legalRepresentativeDocument: null,
                }));
            }

            if (
                typeof corporateInfo.signatoryOnKbis === 'string' ||
                typeof corporateInfo.ownAccountAgreement === 'string' ||
                name === 'signatoryOnKbis' ||
                name === 'ownAccountAgreement'
            ) {
                setErrors(prevState => ({ ...prevState, [name]: '' }));
            }
        },
        [corporateInfo]
    );

    const handleCorporateAddressChange = useCallback(changedAddress => {
        setCorporateInfo(prevValue => ({
            ...prevValue,
            currentAddress: { ...prevValue.currentAddress, ...changedAddress },
        }));
    }, []);

    const handleCorporatePostalAddressChange = useCallback(changedAddress => {
        setCorporateInfo(prevValue => {
            return {
                ...prevValue,
                postalAddress: { ...prevValue.postalAddress, ...changedAddress },
            };
        });
    }, []);

    const toggleCorporateHasDifferentPostalAddress = useCallback(() => {
        setCorporateInfo(prevValue => ({
            ...prevValue,
            hasDifferentPostalAddress: !prevValue.hasDifferentPostalAddress,
        }));
        setCorporateInfo(prevValue => ({
            ...prevValue,
            postalAddress: {
                ...prevValue.postalAddress,
                address: '',
                city: '',
                zipCode: '',
                country: 'FR',
                formattedAddress: null,
            },
        }));
    }, []);

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return (
            !isEqual(defaultForm, {
                generalInfo,
                address,
                ...(hasDifferentFiscalAddress !== undefined &&
                    hasDifferentFiscalAddress !== null && {
                        hasDifferentFiscalAddress,
                    }),
                ...(fiscalAddress && { fiscalAddress }),
                situation,
                fiscalSituation,
                ...(fiscalObligations && { fiscalObligations }),
                ...(politicallyExposed && { politicallyExposed }),
                ...(conditions && { conditions }),
            }) || !isEqual(defaultCorporateInfo, corporateInfo)
        );
    }, [
        generalInfo,
        address,
        hasDifferentFiscalAddress,
        fiscalAddress,
        situation,
        fiscalSituation,
        fiscalObligations,
        politicallyExposed,
        conditions,
        defaultForm,
        defaultCorporateInfo,
        corporateInfo,
    ]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    const handleSubmit = useCallback(
        afterSubmitCallback => isFormValid => {
            reset();
            resetInvestorProfile();
            if (isFormValid || isOnAdminSpace) {
                const payload = {
                    generalInfo,
                    address,
                    hasDifferentFiscalAddress,
                    fiscalAddress,
                    situation,
                    fiscalSituation,
                    fiscalObligations,
                    politicallyExposed,
                    conditions,
                    corporateInfo,
                };
                if (isOnClientSpace || isOnAdminSpace) {
                    updateUser({
                        id: specificUser?.id || internalUser.id,
                        profile:
                            subscription?.role?.name === RoleNames.PP_PROFESSIONNEL ? 'PP' : 'PM',
                        ...payload,
                    });
                } else {
                    sendInvestorProfile({
                        resubmit: afterSubmitCallback ? false : isPhoneVerified,
                        callback: afterSubmitCallback,
                        ...payload,
                    });
                }
            }
        },
        [
            address,
            conditions,
            corporateInfo,
            fiscalAddress,
            fiscalObligations,
            fiscalSituation,
            generalInfo,
            hasDifferentFiscalAddress,
            internalUser,
            isOnClientSpace,
            isOnAdminSpace,
            specificUser,
            isPhoneVerified,
            politicallyExposed,
            situation,
            updateUser,
            subscription,
            reset,
            sendInvestorProfile,
            resetInvestorProfile,
        ]
    );

    useEffect(() => {
        if (investorProfileError) {
            if (
                investorProfileError.data?.errors?.find(
                    err => err.message === investorErrors.CONTACT_INFO_US_PERSON
                )
            ) {
                dispatch(
                    ineligibleSet({
                        open: true,
                        message: texts.LA_SOUSCRIPTION_N_EST_PAS_POSSIBLE,
                    })
                );
            } else if (investorProfileError?.data?.message === investorErrors.INVALID_PHONE) {
                setErrors(prevState => ({
                    ...prevState,
                    phoneNumber: texts.NE_SEMBLE_PAS_ETRE_UN_NUMERO_DE_TELEPHONE,
                }));
            } else if (
                investorProfileError?.data?.errors?.[0]?.message ===
                investorErrors.INVALID_PHONE_NUMBER
            ) {
                setErrors(prevState => ({
                    ...prevState,
                    phoneNumber: texts.INVALID_PHONE,
                }));
            }
        }
    }, [dispatch, investorProfileError]);

    useEffect(() => {
        if (investorProfileSuccess) {
            setErrors(prevState => ({ ...prevState, phoneNumber: '' }));
            if (isPhoneVerified || subscription?.indirectUntransferred) {
                handleNextStep?.(true);
            }
        }
    }, [handleNextStep, investorProfileSuccess, isPhoneVerified, subscription]);

    useEffect(() => {
        if (!mounted.current) return;

        if (isError && error?.data?.detail) {
            let errorKey = error.data.detail.split(':')[0];
            setErrors(prevState => ({ ...prevState, [errorKey]: 'error' }));
        }
    }, [isError, error]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isStepVerified),
        [handleSubmit, checkChanges, isStepVerified, setTunnelChangesHandlers]
    );

    useEffect(() => {
        if (isSuccess) {
            reset();
            refetch?.();
        }
    }, [isSuccess, reset, refetch]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const props = useMemo(
        () => ({
            isStepVerified,
            isPhoneVerified,
            onSubmit: handleSubmit(),
            errors,
            fileSizeError,
            fileTypeError,
            isOnClientSpace,
            isOnAdminSpace,
            forms: {
                generalInfo,
                address,
                hasDifferentFiscalAddress,
                fiscalAddress,
                situation,
                fiscalSituation,
                fiscalObligations,
                politicallyExposed,
                conditions,
                corporateInfo,
            },
            callbacks: {
                handleGeneralInfoChange,
                handleAddressChange,
                toggleHasDifferentFiscalAddress,
                handleFiscalAddressChange,
                handleSituationChange,
                handleFiscalSituationChange,
                handleFiscalObligationChange,
                handleAddFiscalObligation,
                handleRemoveFiscalObligation,
                handleChangePoliticallyExposedPerson,
                handleChangePoliticallyExposedReason,
                handleConditionsChange,
                handleFileUpload,
                handleCorporateInfoChange,
                handleCorporateAddressChange,
                toggleCorporateHasDifferentPostalAddress,
                handleCorporatePostalAddressChange,
            },
            tunnelErrors,
            changesMade: changesMade || !hasStatusOk,
            onAdditionalErrors: handleAdditionalErrors,
            validateDefaultSchema,
            handleNextStep,
            areDefaultFieldsCompleted,
            submitLoader: submitLoader || updateLoader || codeLoader,
            tunnelSubscription: subscription,
            showSuccessMessage: isSuccess,
        }),
        [
            address,
            areDefaultFieldsCompleted,
            isOnClientSpace,
            isOnAdminSpace,
            changesMade,
            codeLoader,
            conditions,
            corporateInfo,
            errors,
            fileSizeError,
            fileTypeError,
            fiscalAddress,
            fiscalObligations,
            fiscalSituation,
            generalInfo,
            handleAddFiscalObligation,
            handleAddressChange,
            handleChangePoliticallyExposedPerson,
            handleChangePoliticallyExposedReason,
            handleConditionsChange,
            handleCorporateAddressChange,
            handleCorporateInfoChange,
            handleCorporatePostalAddressChange,
            handleFileUpload,
            handleFiscalAddressChange,
            handleFiscalObligationChange,
            handleFiscalSituationChange,
            handleGeneralInfoChange,
            handleNextStep,
            handleRemoveFiscalObligation,
            handleSituationChange,
            handleSubmit,
            hasDifferentFiscalAddress,
            hasStatusOk,
            submitLoader,
            isPhoneVerified,
            isStepVerified,
            politicallyExposed,
            isSuccess,
            situation,
            subscription,
            toggleCorporateHasDifferentPostalAddress,
            toggleHasDifferentFiscalAddress,
            tunnelErrors,
            updateLoader,
            validateDefaultSchema,
        ]
    );

    if (subscription?.role?.name === RoleNames.PP_PROFESSIONNEL) {
        return <ContactInfoFormPP {...props} />;
    }
    return <ContactInfoFormPM {...props} />;
};

export default ContactInfoPage;
